import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form as FinalForm } from 'react-final-form';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { required } from '../../util/validators';
import { unlockPrelaunch } from '../../util/api';

import { logout } from '../../ducks/auth.duck';
import {
  PRELAUNCH_PHASE_COUNTDOWN,
  PRELAUNCH_PHASE_GROWERS,
  isPrelaunchClosed,
} from '../../ducks/prelaunch.duck';

import {
  Form,
  FieldTextInput,
  InlineTextButton,
  Logo,
  NamedLink,
  PrimaryButton,
} from '../../components';

import css from './PrelaunchGate.module.css';

const LAUNCH_TIME_ZONE = 'Pacific/Auckland';
const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

const timeLeft = (launchAt, now) => {
  const ms = Math.max(0, new Date(launchAt).getTime() - now);
  return {
    total: ms,
    days: Math.floor(ms / DAY),
    hours: Math.floor((ms % DAY) / HOUR),
    minutes: Math.floor((ms % HOUR) / MINUTE),
    seconds: Math.floor((ms % MINUTE) / SECOND),
  };
};

const Countdown = props => {
  const { launchAt, now, mounted } = props;
  const left = timeLeft(launchAt, now);
  const units = ['days', 'hours', 'minutes', 'seconds'];

  if (left.total === 0) {
    return (
      <p className={css.openingNow}>
        <FormattedMessage id="PrelaunchGate.openingNow" />
      </p>
    );
  }

  return (
    <div className={css.countdown} role="timer">
      {units.map(unit => (
        <div key={unit} className={css.unit}>
          {/* The numbers are only shown in the browser, so they match the visitor's clock */}
          <span className={css.number}>{mounted ? String(left[unit]).padStart(2, '0') : '--'}</span>
          <span className={css.unitLabel}>
            <FormattedMessage id={`PrelaunchGate.${unit}`} />
          </span>
        </div>
      ))}
    </div>
  );
};

const GrowerPasswordForm = props => {
  const intl = useIntl();
  const [error, setError] = useState(null);

  const onSubmit = ({ password }) => {
    setError(null);
    return unlockPrelaunch({ password })
      .then(() => {
        // The server has set the access cookie: reload to open the site
        window.location.reload();
      })
      .catch(e => {
        setError(e?.code === 'wrong-password' ? 'wrongPassword' : 'unlockFailed');
      });
  };

  return (
    <FinalForm
      onSubmit={onSubmit}
      render={({ handleSubmit, submitting, invalid }) => (
        <Form className={css.growerForm} onSubmit={handleSubmit}>
          <FieldTextInput
            id="prelaunchGrowerPassword"
            name="password"
            type="password"
            autoComplete="current-password"
            autoFocus={props.autoFocus}
            label={intl.formatMessage({ id: 'PrelaunchGate.passwordLabel' })}
            validate={required(intl.formatMessage({ id: 'PrelaunchGate.passwordRequired' }))}
          />
          {error ? (
            <p className={css.error} role="alert">
              <FormattedMessage id={`PrelaunchGate.${error}`} />
            </p>
          ) : null}
          <PrimaryButton
            className={css.enterButton}
            type="submit"
            inProgress={submitting}
            disabled={invalid}
          >
            <FormattedMessage id="PrelaunchGate.enter" />
          </PrimaryButton>
        </Form>
      )}
    />
  );
};

/**
 * GardenPlace pre-launch screen, shown over the (blurred) front page while the site is closed
 * (see server/api-util/prelaunch.js):
 * - 'growers' phase: a "coming soon" message and the grower password box
 * - 'countdown' phase: a countdown to launch, sign up / log in, and a link for growers
 * When the countdown reaches zero, the page reloads and the site is open.
 *
 * @component
 * @returns {JSX.Element|null}
 */
const PrelaunchGate = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const closed = useSelector(isPrelaunchClosed);
  const { phase, launchAt } = useSelector(state => state.prelaunch || {});
  const currentUser = useSelector(state => state.user?.currentUser);

  const [mounted, setMounted] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const [showGrowerForm, setShowGrowerForm] = useState(false);

  const isCountdown = phase === PRELAUNCH_PHASE_COUNTDOWN && !!launchAt;

  useEffect(() => {
    setMounted(true);
    if (!closed || !isCountdown) {
      return undefined;
    }
    const interval = window.setInterval(() => {
      const current = Date.now();
      setNow(current);
      if (current >= new Date(launchAt).getTime()) {
        window.clearInterval(interval);
        // Give the server a moment, then load the open site
        window.setTimeout(() => window.location.reload(), 2 * SECOND);
      }
    }, SECOND);
    return () => window.clearInterval(interval);
  }, [closed, isCountdown, launchAt]);

  if (!closed) {
    return null;
  }

  const user = currentUser?.id ? currentUser : null;
  const firstName = user?.attributes?.profile?.firstName;
  const emailVerified = user?.attributes?.emailVerified;
  const opensOn = launchAt
    ? intl.formatDate(new Date(launchAt), {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZone: LAUNCH_TIME_ZONE,
      })
    : null;

  return (
    <div className={css.root}>
      <div className={css.card} role="dialog" aria-modal="true" aria-labelledby="prelaunchHeading">
        <div className={css.logoWrapper}>
          <Logo layout="desktop" />
        </div>

        {phase === PRELAUNCH_PHASE_GROWERS || !isCountdown ? (
          <>
            <h1 id="prelaunchHeading" className={css.heading}>
              <FormattedMessage id="PrelaunchGate.growersHeading" />
            </h1>
            <p className={css.text}>
              <FormattedMessage id="PrelaunchGate.growersText" />
            </p>
            <GrowerPasswordForm />
          </>
        ) : (
          <>
            <h1 id="prelaunchHeading" className={css.heading}>
              <FormattedMessage id="PrelaunchGate.countdownHeading" />
            </h1>
            <p className={css.label}>
              <FormattedMessage id="PrelaunchGate.openingIn" />
            </p>
            <Countdown launchAt={launchAt} now={now} mounted={mounted} />
            <p className={css.opensOn}>
              <FormattedMessage id="PrelaunchGate.opensOn" values={{ date: opensOn }} />
            </p>

            {user ? (
              <div className={css.allSet}>
                <p className={css.allSetHeading}>
                  <FormattedMessage id="PrelaunchGate.allSet" values={{ name: firstName }} />
                </p>
                <p className={css.text}>
                  <FormattedMessage id="PrelaunchGate.allSetText" />
                </p>
                {emailVerified === false ? (
                  <p className={css.text}>
                    <FormattedMessage id="PrelaunchGate.verifyEmail" />
                  </p>
                ) : null}
                <InlineTextButton className={css.textLink} onClick={() => dispatch(logout())}>
                  <FormattedMessage id="PrelaunchGate.logOut" />
                </InlineTextButton>
              </div>
            ) : (
              <>
                <p className={css.text}>
                  <FormattedMessage id="PrelaunchGate.countdownText" />
                </p>
                <NamedLink name="SignupPage" className={css.signupButton}>
                  <FormattedMessage id="PrelaunchGate.signUp" />
                </NamedLink>
                <NamedLink name="LoginPage" className={css.textLink}>
                  <FormattedMessage id="PrelaunchGate.logIn" />
                </NamedLink>
              </>
            )}

            <div className={css.growerSection}>
              {showGrowerForm ? (
                <GrowerPasswordForm autoFocus />
              ) : (
                <InlineTextButton className={css.textLink} onClick={() => setShowGrowerForm(true)}>
                  <FormattedMessage id="PrelaunchGate.growerLink" />
                </InlineTextButton>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PrelaunchGate;
